import React from "react";
import { Modal, Tooltip, Button, message } from "antd";
import {
  workflowThreeById,
} from "../../../../libs/apis";
import { WorkflowThreeI } from "../../../../libs/interfaces";
import { Box, Grid } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
} from "@ant-design/icons";
import { OneCard, OneVideo, ProcessStateAlert } from "../../../../libs/shared";

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: "naviera" | "exporter" | "other" | "gps" | "enginery" | "video";
}

interface ShowContainerPropsI {
  id: number;
}

export const ShowContainer = ({ id }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };
  
  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer
          open={open}
          onClose={handleClose}
          id={id}
        />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

export const ModalContainer = ({
  open,
  onClose,
  id,
}: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<WorkflowThreeI>({
    container: "",
  } as WorkflowThreeI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = React.useState<OnePhotoI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as WorkflowThreeI);
      setLoading(true);
    };
  }, []);

  const handleGetData = async () => {
    try {
      let dataContainer: WorkflowThreeI;
      const { data: dataContainerGet } = await workflowThreeById(id);
      if (!dataContainerGet) return;
      dataContainer = dataContainerGet;
      setData(dataContainerGet);

      const lasFotos: string[] = [];
      const arrCards: OneCardI[] = [
        {
          title: "Foto Panorámica",
          photos: [
            {
              src: dataContainer.containerPanoramicPhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.containerPanoramicComment,
          type: "naviera",
        },
        {
          title: "Foto Botella Naviera",
          photos: [
            {
              src: dataContainer.navieraBottlePhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.navieraBottleComment,
          type: "naviera",
        },
        {
          title: "Foto Etiqueta Naviera",
          photos: [
            {
              src: dataContainer.navieraLabelPhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.navieraLabelComment,
          type: "naviera",
        },
        {
          title: "Foto Cable Naviera",
          photos: [
            {
              src: dataContainer.navieraWirePhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.navieraWireComment,
          type: "naviera",
        },
        {
          title: "Foto Botella Exportador",
          photos: [
            {
              src: dataContainer.exporterBottlePhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.exporterBottleComment,
          type: "exporter",
        },
        {
          title: "Foto Etiqueta Exportador",
          photos: [
            {
              src: dataContainer.exporterLabelPhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.exporterLabelComment,
          type: "exporter",
        },
        {
          title: "Foto Cable Exportador",
          photos: [
            {
              src: dataContainer.exporterWirePhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.exporterWireComment,
          type: "exporter",
        },
        {
          title: "Foto Botella Otro",
          photos: [
            {
              src: dataContainer.otherBottlePhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.otherBottleComment,
          type: "other",
        },
        {
          title: "Foto Etiqueta Otro",
          photos: [
            {
              src: dataContainer.otherLabelPhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.otherLabelComment,
          type: "other",
        },
        {
          title: "Foto Cable Otro",
          photos: [
            {
              src: dataContainer.otherWirePhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.otherWireComment,
          type: "other",
        },
        {
          title: "Foto Gps",
          photos: [
            {
              src: dataContainer.gpsPhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.gpsComment,
          type: "gps",
        },
        {
          title: "Foto Sello Gps",
          photos: [
            {
              src: dataContainer.gpsStampPhoto,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.gpsStampComment,
          type: "gps",
        },
        {
          title: "Foto Maquinaria 1",
          photos: [
            {
              src: dataContainer.engineryPhoto1,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.engineryComment1,
          type: "enginery",
        },
        {
          title: "Foto Maquinaria 2",
          photos: [
            {
              src: dataContainer.engineryPhoto2,
              alt: "Foto",
              showAlt: true,
            },
          ],
          value: dataContainer.engineryComment2,
          type: "enginery",
        },
      ];

      arrCards.forEach((photo) => {
        // if(photo.photos.length > 1) {
        photo.photos.forEach((photo) => {
          lasFotos.push(photo.src as string);
        });
      });
      const arrVideos: OnePhotoI[] = [
        { src: dataContainer.engineryVideo, alt: "Video Maquinaria" },
        { src: dataContainer.doorVideo, alt: "Video Puerta" },
      ];

      setTotalPhotos(lasFotos);
      setCardVideos(arrVideos);
      setCards(arrCards);
      setLoading(false);
    } catch (err: any) {
      message.error((err as Error).message);
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <p
          style={{
            color: "white",
            padding: "10px",
            fontWeight: "bold",
            fontSize: "1.2rem",
            textAlign: "center",
            background: "linear-gradient(to bottom, #FFA500, #FF4500)", // Naranja a rojo
            borderRadius: "5px",
            width: "98%",
            margin: "0 auto",
          }}
        >
          {`CONTENEDOR N° ${data.container}`}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{
            background: "red",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={810}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      style={{ padding: 0 }}
    >
      <Box sx={{ overflowY: "auto", maxHeight: 550, p: 1 }}>
        <ProcessStateAlert
          state={data.processState}
          reason={data.processStateReason}
        />
        <DataSection data={data} />
        <PhotoSection
          title="Naviera"
          type="naviera"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Exportador"
          type="exporter"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Otras"
          type="other"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Gps"
          type="gps"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Maquinaria"
          type="enginery"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        {/* <VideoSection videos={cardVideos} /> */}
      </Box>
    </Modal>
  );
};

const DataSection = ({ data }: { data: WorkflowThreeI }) => {
  const rows = [
    { name: "Puerto de Ingreso", value: data.entryPort },
    ...(data.extraFields || []).filter((f) => f.name),
  ].filter((r) => r.value);

  if (rows.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          textAlign: "center",
          mb: 2,
          mt: 2,
          bgcolor: "transparent",
          borderRadius: "4px",
          background:
            "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
        }}
      >
        <h3 style={{ color: "white" }}>Datos del Proceso</h3>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        }}
      >
        {rows.map((r, idx) => (
          <Box
            key={`${r.name}-${idx}`}
            sx={{
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              p: 1,
              wordBreak: "break-word",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: "bold", color: "#555" }}>
              {r.name}
            </div>
            <div style={{ fontSize: 14 }}>{r.value}</div>
          </Box>
        ))}
      </Box>
    </>
  );
};

const PhotoSection = ({
  title,
  type,
  cards,
  totalPhotos,
}: {
  title: string;
  type: string;
  cards: OneCardI[];
  totalPhotos: string[];
}) => (
  <>
    <Box
      sx={{
        textAlign: "center",
        mb: 2,
        mt: 4,
        bgcolor: "transparent",
        borderRadius: "4px",
        background:
          "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
      }}
    >
      <h3 style={{ color: "white" }}>{title}</h3>
    </Box>
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      }}
    >
      {cards
        .filter((el) => el.type === type)
        .map((el) => (
          <OneCard data={el} key={el.title} lasFotos={totalPhotos} />
        ))}
    </Box>
  </>
);

const VideoSection = ({ videos }: { videos: OnePhotoI[] }) => (
  <>
    <Box
      sx={{
        textAlign: "center",
        mb: 4,
        mt: 8,
        bgcolor: "transparent",
        borderRadius: "4px",
        background:
          "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
      }}
    >
      <h3 style={{ color: "white" }}>Videos</h3>
    </Box>
    <Grid container spacing={2} justifyContent={"center"}>
      {videos.map((video, index) => (
        <Grid item xs={12} sm={6} md={4}  key={index}>
          <OneVideo src={video.src!} alt={video.alt} />
        </Grid>
      ))}
    </Grid>
  </>
);